import type { Node, Edge } from '@xyflow/react';
import { IOType } from '@erp/shared-types/node-graph';
import type { WorkflowTemplateResponse } from '@erp/rpc';

interface MMGraphSocket {
  id: string;
  ioType: IOType;
}

interface NodeDataType extends Record<string, unknown> {
  label: string;
  inputs: {
    [key: string]: MMGraphSocket;
  };
  outputs: {
    [key: string]: MMGraphSocket;
  };
  stepType?: string;
  emits?: string[];
}

/**
 * Convert a workflow template to React Flow nodes and edges
 */
export function templateToGraph(template: WorkflowTemplateResponse): {
  nodes: Node<NodeDataType>[];
  edges: Edge[];
} {
  // Convert template steps to React Flow nodes
  const nodes: Node<NodeDataType>[] = template.steps.map((step, index) => {
    // Convert sockets to the format expected by StepNode
    const inputs: { [key: string]: MMGraphSocket } = {};
    const outputs: { [key: string]: MMGraphSocket } = {};

    step.sockets.inputs.forEach((socket) => {
      inputs[socket.name] = {
        id: socket.id,
        ioType: IOType.INPUT,
      };
    });

    step.sockets.outputs.forEach((socket) => {
      outputs[socket.name] = {
        id: socket.id,
        ioType: IOType.OUTPUT,
      };
    });

    return {
      id: `step-${index}`,
      type: 'stepNode',
      position: step.position,
      data: {
        label: step.name,
        inputs,
        outputs,
        // Pass through optional fields when available
        stepType: step.stepType,
        emits: step.emits,
      },
    };
  });

  // Convert template edges to React Flow edges
  const edges: Edge[] = template.edges.map((edge, index) => ({
    id: `edge-${index}`,
    source: `step-${edge.sourceStepIndex}`,
    sourceHandle: edge.sourceSocketId,
    target: `step-${edge.targetStepIndex}`,
    targetHandle: edge.targetSocketId,
    animated: true,
  }));

  return { nodes, edges };
}
